import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { ArrowLeft, ArrowRight, TrendingUp, Users, DollarSign, Activity, Target, Clock, Award } from 'lucide-react';
import { strategies } from '../mock';
import { trackLead } from '../utils/fbPixel';

const StrategyDetail = () => {
  const { strategyId } = useParams();
  const navigate = useNavigate();
  
  const strategy = strategies.find(s => s.strategyId === strategyId);

  // Track Lead event when page loads
  useEffect(() => {
    if (strategy) {
      trackLead();
    }
  }, [strategy]);

  if (!strategy) {
    return (
      <div className="strategy-detail-page">
        <div className="detail-container">
          <h2>Strategy not found</h2>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  // Asset allocation data for Pie Chart
  const assetData = strategy.name === 'Algo FX Master' ? [
    { name: 'Metals', value: 76, trades: 3227 },
    { name: 'Crypto', value: 13.5, trades: 573 },
    { name: 'Indices', value: 8.64, trades: 424 },
    { name: 'Forex', value: 1.86, trades: 13 }
  ] : strategy.name === 'Area101' ? [
    { name: 'Gold (XAUUSD)', value: 100, trades: 4383 }
  ] : strategy.name === 'Titan Ai Trades' ? [
    { name: 'XAUUSD', value: 51.64, trades: 173 },
    { name: 'AUDUSD', value: 13.73, trades: 46 },
    { name: 'EURAUD', value: 6.27, trades: 21 },
    { name: 'GBPUSD', value: 15.22, trades: 51 },
    { name: 'USDJPY', value: 13.14, trades: 44 }
  ] : [
    { name: 'Gold (XAUUSD)', value: 100, trades: 871 }
  ];

  // Performance data for Bar Chart
  const performanceData = [
    { month: 'Oct', return: parseFloat(strategy.roi) * 0.3 },
    { month: 'Nov', return: parseFloat(strategy.roi) * 0.4 },
    { month: 'Dec', return: parseFloat(strategy.roi) * 0.3 },
  ];

  // Weekly trades data
  const weeklyData = strategy.name === 'Trading Assassin 007' ? [
    { week: 'Week 1', trades: 52 },
    { week: 'Week 2', trades: 61 },
    { week: 'Week 3', trades: 58 },
    { week: 'Week 4', trades: 64 }
  ] : [
    { week: 'Week 1', trades: 28 },
    { week: 'Week 2', trades: 31 },
    { week: 'Week 3', trades: 26 },
    { week: 'Week 4', trades: 29 }
  ];

  const COLORS = ['#C9A04B', '#00D9FF', '#4ADE80', '#FF6B6B', '#A78BFA', '#FB923C', '#EC4899', '#14B8A6'];

  return (
    <div className="strategy-detail-page">
      {/* Header */}
      <header className="sahara-header">
        <div className="header-content">
          <div className="logo-container" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/images/sahara-logo.png" alt="Sahara AI Ecosystem" className="header-logo" />
          </div>
          <Button className="btn-primary-sahara" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </header>

      <div className="detail-container">
        {/* Back Button */}
        <Button className="back-button" onClick={() => navigate('/')}>
          <ArrowLeft className="btn-icon" />
          Back to Strategies
        </Button>

        {/* Strategy Header */}
        <div className="detail-header">
          <div className="detail-header-left">
            <h1 className="detail-title">{strategy.name}</h1>
            <p className="detail-id">ID: {strategy.strategyId}</p>
            <p className="detail-provider">Provider: {strategy.provider}</p>
            <div className="detail-tags">
              <span className="tag-style">{strategy.style}</span>
              <span className="tag-risk">{strategy.drawdown}</span>
            </div>
          </div>
          <div className="detail-header-right">
            <Button className="btn-primary-sahara btn-large" onClick={() => {
              trackLead();
              window.open('https://t.me/saharacopytrade', '_blank');
            }}>
              Copy Strategy Now
              <TrendingUp className="btn-icon" />
            </Button>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="metrics-grid">
          <Card className="metric-card">
            <CardContent className="metric-content">
              <DollarSign className="metric-icon" />
              <div>
                <p className="metric-label">Return (3M)</p>
                <p className="metric-value">{strategy.roi}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardContent className="metric-content">
              <Activity className="metric-icon" />
              <div>
                <p className="metric-label">Win Rate</p>
                <p className="metric-value">{strategy.winRate}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardContent className="metric-content">
              <Users className="metric-icon" />
              <div>
                <p className="metric-label">Copiers</p>
                <p className="metric-value">{strategy.copiers}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardContent className="metric-content">
              <Target className="metric-icon" />
              <div>
                <p className="metric-label">Profit Sharing</p>
                <p className="metric-value">{strategy.profitSharing}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="charts-grid">
          {/* Asset Allocation Pie Chart */}
          <Card className="chart-card">
            <CardHeader>
              <CardTitle className="chart-title">Asset Allocation</CardTitle>
              <p className="chart-subtitle">Trade distribution by asset class</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={assetData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {assetData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="asset-legend">
                {assetData.map((asset, index) => (
                  <div key={index} className="legend-item">
                    <div className="legend-color" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                    <span>{asset.name}: {asset.trades} trades</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Performance Bar Chart */}
          <Card className="chart-card">
            <CardHeader>
              <CardTitle className="chart-title">Monthly Performance</CardTitle>
              <p className="chart-subtitle">Return percentage by month</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(201, 160, 75, 0.1)" />
                  <XAxis dataKey="month" stroke="#F5F1E5" />
                  <YAxis stroke="#F5F1E5" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(10, 10, 10, 0.9)', 
                      border: '1px solid #C9A04B',
                      borderRadius: '4px'
                    }} 
                  />
                  <Bar dataKey="return" fill="#C9A04B" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Weekly Trades Bar Chart */}
          <Card className="chart-card">
            <CardHeader>
              <CardTitle className="chart-title">Weekly Trading Activity</CardTitle>
              <p className="chart-subtitle">Number of trades per week</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(201, 160, 75, 0.1)" />
                  <XAxis dataKey="week" stroke="#F5F1E5" />
                  <YAxis stroke="#F5F1E5" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(10, 10, 10, 0.9)', 
                      border: '1px solid #C9A04B',
                      borderRadius: '4px'
                    }} 
                  />
                  <Bar dataKey="trades" fill="#00D9FF" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Additional Stats */}
          <Card className="chart-card">
            <CardHeader>
              <CardTitle className="chart-title">Trading Statistics</CardTitle>
              <p className="chart-subtitle">Key performance indicators</p>
            </CardHeader>
            <CardContent>
              <div className="stats-list">
                <div className="stat-item">
                  <Award className="stat-icon" />
                  <div>
                    <p className="stat-label">Total Trades</p>
                    <p className="stat-value">{strategy.totalTrades}</p>
                  </div>
                </div>
                <div className="stat-item">
                  <DollarSign className="stat-icon" />
                  <div>
                    <p className="stat-label">Return YTD</p>
                    <p className="stat-value">{strategy.returnYTD}</p>
                  </div>
                </div>
                <div className="stat-item">
                  <TrendingUp className="stat-icon" />
                  <div>
                    <p className="stat-label">Max Daily Return</p>
                    <p className="stat-value">{strategy.maxDailyReturn}</p>
                  </div>
                </div>
                {strategy.avgHoldingTime && (
                  <div className="stat-item">
                    <Clock className="stat-icon" />
                    <div>
                      <p className="stat-label">Avg Holding Time</p>
                      <p className="stat-value">{strategy.avgHoldingTime}</p>
                    </div>
                  </div>
                )}
                {strategy.tradesPerWeek && (
                  <div className="stat-item">
                    <Activity className="stat-icon" />
                    <div>
                      <p className="stat-label">Trades Per Week</p>
                      <p className="stat-value">{strategy.tradesPerWeek}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <Card className="cta-card">
          <CardContent className="cta-content">
            <h2 className="cta-title">Ready to Copy This Strategy?</h2>
            <p className="cta-description">
              Join {strategy.copiers} traders already copying {strategy.name}. Start earning automatically today.
            </p>
            <Button className="btn-primary-sahara btn-xl" onClick={() => {
              trackLead();
              window.open('https://t.me/saharacopytrade', '_blank');
            }}>
              Start Copying Now
              <ArrowRight className="btn-icon" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StrategyDetail;
